import React, {useState, useEffect} from 'react';
import {Button, Table, message, Tag, Input, Select, Checkbox, Card, Tooltip} from 'antd';
import {EditOutlined, CheckOutlined, CloseOutlined, PlusOutlined, DeleteOutlined, QuestionCircleOutlined} from '@ant-design/icons';
import {useParams, useNavigate} from 'react-router-dom';
import {useQuery, useMutation} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import type {ColumnsType} from 'antd/es/table';
import {GET_SORTER_PLAN_DWS_DETAIL} from './graphql/queries/getSorterPlanDwsDetail';
import {BATCH_SAVE_SORTER_PLAN_DWS_CHUTES} from './graphql/mutations/saveDwsChuteConfigs';
import {SAVE_SORTER_PLAN} from './graphql/mutations/saveSorterPlan';
import {useWarehouseOptions} from '../../../hooks/useWarehouseOptions';

interface DwsChuteRuleRecord {
    key: string;
    id?: number;
    ruleNumber: number;
    isOverspecification: boolean;
    isNone: boolean;
    sortingCodes: string;
    chuteCode: string;
}

const SorterPlanDetailDWSPage: React.FC = () => {
    const {t} = useTranslation();
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const [saving, setSaving] = useState(false);
    const [ruleData, setRuleData] = useState<DwsChuteRuleRecord[]>([]);
    const [editingPlanName, setEditingPlanName] = useState(false);
    const [planNameValue, setPlanNameValue] = useState('');
    const [exceptionChuteCode, setExceptionChuteCode] = useState<string>('');
    const [chuteCodeOptions, setChuteCodeOptions] = useState<{value: string; label: string}[]>([]);
    const [shouldNavigateAfterSave, setShouldNavigateAfterSave] = useState(false); // 标记是否需要跳转

    // 查询DWS详情和格口配置
    const {data: dwsData, loading, refetch} = useQuery(GET_SORTER_PLAN_DWS_DETAIL, {
        variables: {id: id ? parseInt(id) : 0},
        skip: !id,
        onCompleted: (data) => {
            if (data?.getSorterPlanDwsDetail) {
                // 验证是否是DWS类型（sorterType = 1）
                if (data.getSorterPlanDwsDetail.sorterType !== 1) {
                    message.error(t('wms.sorterPlan.dws.messages.notDwsType'));
                    navigate('/wms/sorterPlan', {replace: true});
                    return;
                }

                // 设置异常兜底格口号
                if (data.getSorterPlanDwsDetail.exceptionChuteCode) {
                    setExceptionChuteCode(data.getSorterPlanDwsDetail.exceptionChuteCode);
                }

                // 从 sorter.chuteConfig 中解析格口号选项
                if (data.getSorterPlanDwsDetail.chuteConfig) {
                    try {
                        const chuteCodes = data.getSorterPlanDwsDetail.chuteConfig.split(',').map((code: string) => code.trim());
                        const options = chuteCodes.map((code: string) => ({value: code, label: code}));
                        setChuteCodeOptions(options);
                    } catch (error) {
                        console.error('解析格口号配置失败:', error);
                        message.error(t('wms.sorterPlan.dws.messages.chuteConfigFormatError'));
                    }
                }

                // 转换为规则格式（只包含非异常类型的格口）
                if (data.getSorterPlanDwsDetail.dwsChuteConfigs) {
                    const rules = data.getSorterPlanDwsDetail.dwsChuteConfigs.map((config: any, index: number) => ({
                        key: config.id ? `${config.id}` : `new-${index}`,
                        id: config.id,
                        ruleNumber: index + 1,
                        isOverspecification: config.isOverspecification || false,
                        isNone: config.isNone || false,
                        sortingCodes: config.sortingCodes || '',
                        chuteCode: config.chuteCode || ''
                    }));
                    setRuleData(rules);
                }
            }
        }
    });

    const planDetail = dwsData?.getSorterPlanDwsDetail;

    // 作业仓名称回显（只读）
    const {warehouseOptions: warehouseNameOptions} = useWarehouseOptions('ALL');
    const workWarehouseName = planDetail?.warehouseId
        ? warehouseNameOptions.find(w => w.id === String(planDetail.warehouseId))?.name
        : undefined;

    // 初始化方案名称编辑值
    useEffect(() => {
        if (planDetail?.sorterPlanName) {
            setPlanNameValue(planDetail.sorterPlanName);
        }
    }, [planDetail?.sorterPlanName]);

    // 更新方案名称
    const [updateSorterPlan] = useMutation(SAVE_SORTER_PLAN, {
        onCompleted: () => {
            message.success(t('wms.sorterPlan.messages.updateSuccess'));
            setEditingPlanName(false);
            refetch();
        },
        onError: (error) => {
            console.error('Update sorter plan error:', error);
            message.error(t('wms.sorterPlan.messages.updateFailed'));
        }
    });

    // 批量保存DWS格口配置
    const [batchSaveDwsChutes] = useMutation(BATCH_SAVE_SORTER_PLAN_DWS_CHUTES, {
        onCompleted: () => {
            message.success(t('wms.sorterPlan.dws.messages.saveSuccess'));
            setSaving(false);

            // 如果是"保存并选中"，则跳转到列表页
            if (shouldNavigateAfterSave) {
                navigate('/wms/sorterPlan');
            } else {
                // 仅保存时刷新数据
                refetch();
            }
        },
        onError: (error) => {
            console.error('Save DWS chute configs error:', error);

            // 优先使用 messageKey 获取国际化错误消息
            let errorMessage = t('wms.sorterPlan.dws.messages.saveFailed');
            if (error?.graphQLErrors?.[0]?.extensions?.messageKey) {
                const messageKey = error.graphQLErrors[0].extensions.messageKey as string;
                const details = error.graphQLErrors[0].extensions.details || {};

                // 将 details 作为参数传递给 t() 函数，用于替换占位符
                const translatedMessage = t(messageKey, details);

                // 如果翻译成功（翻译结果不等于key本身），使用翻译后的消息
                if (translatedMessage && translatedMessage !== messageKey) {
                    errorMessage = translatedMessage;
                } else {
                    // 否则使用原始错误消息
                    errorMessage = error.message || t('wms.sorterPlan.dws.messages.saveFailed');
                }
            } else {
                errorMessage = error.message || t('wms.sorterPlan.dws.messages.saveFailed');
            }

            message.error(errorMessage);
            setSaving(false);
            setShouldNavigateAfterSave(false); // 重置标记
        }
    });

    // 开始编辑方案名称
    const handleStartEditPlanName = () => {
        setEditingPlanName(true);
        setPlanNameValue(planDetail?.sorterPlanName || '');
    };

    // 保存方案名称
    const handleSavePlanName = async () => {
        if (!planNameValue.trim()) {
            message.error(t('wms.sorterPlan.validation.sorterPlanNameRequired'));
            return;
        }

        try {
            await updateSorterPlan({
                variables: {
                    input: {
                        id: parseInt(id!),
                        sorterId: planDetail?.sorterId,
                        sorterPlanName: planNameValue.trim(),
                        status: planDetail?.status,
                        version: planDetail?.version || 0,
                        isDropBag: planDetail?.isDropBag || false,
                        isSpecified: planDetail?.isSpecified || false
                    }
                }
            });
        } catch (error) {
            console.error('Update plan name error:', error);
        }
    };

    // 取消编辑方案名称
    const handleCancelEditPlanName = () => {
        setEditingPlanName(false);
        setPlanNameValue(planDetail?.sorterPlanName || '');
    };

    // 更新规则数据
    const handleUpdateRule = (key: string, field: keyof DwsChuteRuleRecord, value: any) => {
        setRuleData(prev => prev.map(item => 
            item.key === key ? {...item, [field]: value} : item
        ));
    };

    // 添加规则
    const handleAddRule = () => {
        const newRule: DwsChuteRuleRecord = {
            key: `new-${Date.now()}`,
            ruleNumber: ruleData.length + 1,
            isOverspecification: false,
            isNone: true, // 默认选中"是"
            sortingCodes: '',
            chuteCode: ''
        };
        setRuleData([...ruleData, newRule]);
    };

    // 删除规则
    const handleDeleteRule = (key: string) => {
        const newData = ruleData.filter(item => item.key !== key);
        // 重新编号
        const reNumberedData = newData.map((item, index) => ({
            ...item,
            ruleNumber: index + 1
        }));
        setRuleData(reNumberedData);
    };

    // 格式化分拣码：去除逗号前后的空格
    const formatSortingCodes = (sortingCodes: string | undefined): string => {
        if (!sortingCodes || !sortingCodes.trim()) {
            return '';
        }
        // 按逗号分割，去除每个部分的首尾空格，过滤空字符串，再用逗号连接
        return sortingCodes
            .split(',')
            .map(code => code.trim())
            .filter(code => code.length > 0)
            .join(',');
    };

    // 仅保存
    const handleSaveOnly = async () => {
        if (ruleData.length === 0) {
            message.warning(t('wms.sorterPlan.dws.messages.atLeastOneRule'));
            return;
        }

        // 验证：如果"是否有分拣码"为"是"，则分拣码必填
        const invalidRules = ruleData.filter(item => item.isNone && !item.sortingCodes?.trim());
        if (invalidRules.length > 0) {
            const ruleNumbers = invalidRules.map(r => r.ruleNumber).join('、');
            message.error(t('wms.sorterPlan.dws.messages.sortingCodeRequired', { ruleNumbers }));
            return;
        }

        try {
            setSaving(true);
            setShouldNavigateAfterSave(false); // 仅保存，不跳转
            
            const input = {
                sorterPlanId: parseInt(id!),
                exceptionChuteCode: exceptionChuteCode && exceptionChuteCode.trim() ? exceptionChuteCode.trim() : null,
                chuteConfigs: ruleData.map(item => ({
                    id: item.id,
                    chuteCode: item.chuteCode,
                    chuteType: 'NORMAL', // 格口规则都是 NORMAL 类型
                    isOverspecification: item.isOverspecification,
                    isNone: item.isNone,
                    sortingCodes: formatSortingCodes(item.sortingCodes) // 格式化分拣码
                })),
                operateType: 1 // 仅保存
            };

            await batchSaveDwsChutes({ variables: { input } });
        } catch (error) {
            console.error('Save chutes error:', error);
        }
    };

    // 保存并选中
    const handleSaveAndUse = async () => {
        if (ruleData.length === 0) {
            message.warning(t('wms.sorterPlan.dws.messages.atLeastOneRule'));
            return;
        }

        // 验证：如果"是否有分拣码"为"是"，则分拣码必填
        const invalidRules = ruleData.filter(item => item.isNone && !item.sortingCodes?.trim());
        if (invalidRules.length > 0) {
            const ruleNumbers = invalidRules.map(r => r.ruleNumber).join('、');
            message.error(t('wms.sorterPlan.dws.messages.sortingCodeRequired', { ruleNumbers }));
            return;
        }

        try {
            setSaving(true);
            setShouldNavigateAfterSave(true); // 保存并选中，需要跳转

            // 保存格口配置并激活方案
            const input = {
                sorterPlanId: parseInt(id!),
                exceptionChuteCode: exceptionChuteCode && exceptionChuteCode.trim() ? exceptionChuteCode.trim() : null,
                chuteConfigs: ruleData.map(item => ({
                    id: item.id,
                    chuteCode: item.chuteCode,
                    chuteType: 'NORMAL', // 格口规则都是 NORMAL 类型
                    isOverspecification: item.isOverspecification,
                    isNone: item.isNone,
                    sortingCodes: formatSortingCodes(item.sortingCodes) // 格式化分拣码
                })),
                operateType: 2 // 保存并选中
            };

            await batchSaveDwsChutes({ variables: { input } });
        } catch (error) {
            console.error('Save and use error:', error);
        }
    };

    // 取消返回
    const handleCancel = () => {
        navigate('/wms/sorterPlan');
    };

    // 表格列定义
    const columns: ColumnsType<DwsChuteRuleRecord> = [
        {
            title: t('wms.sorterPlan.dws.columns.ruleNumber'),
            dataIndex: 'ruleNumber',
            key: 'ruleNumber',
            width: 100,
            render: (text) => <span>{text}</span>
        },
        {
            title: t('wms.sorterPlan.dws.columns.isOverspecification'),
            dataIndex: 'isOverspecification',
            key: 'isOverspecification',
            width: 150,
            render: (value, record) => (
                <Select
                    value={value ? t('wms.sorterPlan.dws.options.yes') : t('wms.sorterPlan.dws.options.no')}
                    onChange={(val) => handleUpdateRule(record.key, 'isOverspecification', val === t('wms.sorterPlan.dws.options.yes'))}
                    style={{width: '100%'}}
                    options={[
                        {value: t('wms.sorterPlan.dws.options.no'), label: t('wms.sorterPlan.dws.options.no')},
                        {value: t('wms.sorterPlan.dws.options.yes'), label: t('wms.sorterPlan.dws.options.yes')}
                    ]}
                />
            )
        },
        {
            title: t('wms.sorterPlan.dws.columns.isNone'),
            dataIndex: 'isNone',
            key: 'isNone',
            width: 140,
            render: (value, record) => (
                <Select
                    value={value ? t('wms.sorterPlan.dws.options.yes') : t('wms.sorterPlan.dws.options.no')}
                    onChange={(val) => {
                        const newValue = val === t('wms.sorterPlan.dws.options.yes');
                        handleUpdateRule(record.key, 'isNone', newValue);
                        // 如果选择"否"，清空分拣码
                        if (!newValue) {
                            handleUpdateRule(record.key, 'sortingCodes', '');
                        }
                    }}
                    style={{width: '100%'}}
                    options={[
                        {value: t('wms.sorterPlan.dws.options.no'), label: t('wms.sorterPlan.dws.options.no')},
                        {value: t('wms.sorterPlan.dws.options.yes'), label: t('wms.sorterPlan.dws.options.yes')}
                    ]}
                />
            )
        },
        {
            title: t('wms.sorterPlan.dws.columns.sortingCodes'),
            dataIndex: 'sortingCodes',
            key: 'sortingCodes',
            render: (text, record) => (
                <Input
                    value={text}
                    onChange={(e) => handleUpdateRule(record.key, 'sortingCodes', e.target.value)}
                    disabled={!record.isNone}
                    style={{
                        backgroundColor: !record.isNone ? '#f5f5f5' : undefined
                    }}
                />
            )
        },
        {
            title: t('wms.sorterPlan.dws.columns.chuteCode'),
            dataIndex: 'chuteCode',
            key: 'chuteCode',
            width: 150,
            render: (text, record) => (
                <Select
                    value={text || undefined}
                    onChange={(value) => handleUpdateRule(record.key, 'chuteCode', value)}
                    style={{width: '100%'}}
                    placeholder={t('wms.sorterPlan.dws.placeholders.selectChuteCode')}
                    options={chuteCodeOptions}
                />
            )
        },
        {
            title: t('wms.sorterPlan.dws.columns.actions'),
            key: 'action',
            width: 80,
            fixed: 'right' as const,
            render: (_, record) => (
                <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteRule(record.key)}
                >
                    {t('wms.sorterPlan.dws.buttons.delete')}
                </Button>
            )
        }
    ];

    return (
        <div className="flex flex-col h-screen">
            <div className="flex-1 overflow-hidden flex flex-col p-6">
                {/* 头部标题行 */}
                <div className="mb-6 flex items-center gap-3">
                    <div className="w-1 h-8 bg-purple-600"></div>
                    <div className="flex items-center gap-3">
                        {editingPlanName ? (
                            <div className="flex items-center gap-2">
                                <Input
                                    value={planNameValue}
                                    onChange={(e) => setPlanNameValue(e.target.value)}
                                    onPressEnter={handleSavePlanName}
                                    style={{width: 300}}
                                    autoFocus
                                />
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<CheckOutlined />}
                                    onClick={handleSavePlanName}
                                />
                                <Button
                                    size="small"
                                    icon={<CloseOutlined />}
                                    onClick={handleCancelEditPlanName}
                                />
                            </div>
                        ) : (
                            <>
                                <h2 className="text-2xl font-semibold m-0">
                                    {planDetail?.sorterName && planDetail?.sorterPlanName
                                        ? `${planDetail.sorterName} — ${planDetail.sorterPlanName}`
                                        : planDetail?.sorterPlanName || planDetail?.sorterName || '--'}
                                </h2>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={handleStartEditPlanName}
                                />
                            </>
                        )}
                        {planDetail?.status === 1 ? (
                            <Tag color="green" className="text-sm">
                                {t('wms.sorterPlan.status.inUse')}
                            </Tag>
                        ) : (
                            <Tag color="default" className="text-sm">
                                {t('wms.sorterPlan.status.unused')}
                            </Tag>
                        )}
                    </div>
                </div>

                {/* 作业仓库（只读） */}
                <div className="mb-4 flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">
                        {t('wms.sorterPlan.detail.warehouse')}:
                    </span>
                    <span className="text-sm">{workWarehouseName || planDetail?.warehouseId || '--'}</span>
                </div>

                {/* 异常兜底格口号 */}
                <div className="mb-4">
                    <div className="flex items-center gap-4">
                        <h3 className="text-base font-medium mb-0">{t('wms.sorterPlan.dws.exceptionChuteLabel')}</h3>
                        <Select
                            value={exceptionChuteCode || undefined}
                            onChange={(value) => setExceptionChuteCode(value || '')}
                            style={{width: 200}}
                            placeholder={t('wms.sorterPlan.dws.placeholders.selectChuteCode')}
                            allowClear
                            options={chuteCodeOptions}
                        />
                    </div>
                </div>

                {/* 格口规则 */}
                <div className="mb-4">
                    <div className="flex items-center gap-2 mb-3">
                        <h3 className="text-base font-medium mb-0">{t('wms.sorterPlan.dws.chuteRulesTitle')}</h3>
                        <Tooltip
                            title={
                                <div>
                                    <div>{t('wms.sorterPlan.dws.tooltips.chuteCodeHint')}</div>
                                    <div>{t('wms.sorterPlan.dws.tooltips.sortingCodeHint')}</div>
                                </div>
                            }
                            trigger="hover"
                            overlayStyle={{maxWidth: '400px'}}
                        >
                            <QuestionCircleOutlined className="text-gray-400 cursor-help" />
                        </Tooltip>
                    </div>
                    <Table<DwsChuteRuleRecord>
                        columns={columns}
                        dataSource={ruleData}
                        rowKey="key"
                        loading={loading}
                        pagination={false}
                        scroll={{x: 'max-content', y: 'calc(100vh - 450px)'}}
                        size="small"
                    />
                </div>

                {/* 添加规则按钮 */}
                <div className="flex justify-center">
                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={handleAddRule}
                        style={{width: '200px'}}
                    >
                        {t('wms.sorterPlan.dws.buttons.addRule')}
                    </Button>
                </div>
            </div>

            {/* 底部固定按钮栏 */}
            <div className="border-t border-gray-200 bg-white px-6 py-4 flex justify-between items-center flex-shrink-0">
                <Button
                    type="primary"
                    onClick={handleSaveAndUse}
                    loading={saving}
                    disabled={ruleData.length === 0}
                >
                    {t('wms.sorterPlan.dws.buttons.saveAndUse')}
                </Button>
                <div className="flex gap-3">
                    <Button
                        onClick={handleSaveOnly}
                        loading={saving}
                        disabled={ruleData.length === 0 || planDetail?.status === 1}
                    >
                        {t('wms.sorterPlan.dws.buttons.saveOnly')}
                    </Button>
                    <Button
                        onClick={handleCancel}
                        disabled={saving}
                    >
                        {t('wms.sorterPlan.dws.buttons.cancel')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SorterPlanDetailDWSPage;
